import React, { createContext, useState, useEffect, useContext } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Vérifier d'abord le localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && savedTheme !== 'auto') {
      return savedTheme;
    }

    // Si 'auto' ou pas de préférence sauvegardée, détecter le thème de l'OS
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const [themePreference, setThemePreference] = useState(() => {
    return localStorage.getItem('theme') || 'auto';
  });

  // Écouter les changements du thème de l'OS
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Ne mettre à jour que si l'utilisateur a choisi 'auto'
      if (themePreference === 'auto') {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  // Appliquer le thème au document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.className = theme === 'dark' ? 'dark-mode' : 'light-mode';
  }, [theme]);

  const changeTheme = (newPreference) => {
    setThemePreference(newPreference);
    localStorage.setItem('theme', newPreference);

    if (newPreference === 'auto') {
      // Détecter le thème de l'OS
      const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(isDark ? 'dark' : 'light');
    } else {
      setTheme(newPreference);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, themePreference, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
